import * as React from 'react';

import { Alert, Avatar, Box, BoxProps, Typography, styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import axios from 'axios';
import { useField } from 'formik';

import { uploadFileToS3 } from '../../../../../api/files/uploadFileToS3';
import { environment } from '../../../../../config';

type PhotoCardProps = BoxProps<'label'> & {
  error?: boolean;
};

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

const PhotoCard = styled(Box, { shouldForwardProp: (prop) => prop !== 'error' })<PhotoCardProps>(
  ({ theme, error }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px dashed #939393`,
    borderColor: error ? theme.palette.error.light : undefined,
    borderRadius: '8px',
    backgroundColor: '#FEFEFE',
    width: '100%',
    cursor: 'pointer',
    minHeight: 340,
  }),
);

// Image validation constants
const IMAGE_REQUIREMENTS = {
  RECOMMENDED_WIDTH: 2000,
  RECOMMENDED_HEIGHT: 2000,
  MIN_SIZE: 200 * 1024, // 200KB in bytes
  MAX_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ACCEPTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
};

type ValidationError = {
  type: 'size' | 'dimensions' | 'format';
  message: string;
};

type Props = {
  isShopifyProduct?: boolean;
};

const OfferPhoto: React.FC<Props> = ({ isShopifyProduct = false }) => {
  const [field, meta, helper] = useField<string>('offerImageKey');
  const [previewImg, setPreviewImg] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>();
  const [isLocalSet, setIsLocalSet] = React.useState(false);
  const [validationError, setValidationError] = React.useState<ValidationError | null>(null);

  const validateImage = (file: File): Promise<ValidationError | null> => {
    return new Promise((resolve) => {
      // Check file format
      if (!IMAGE_REQUIREMENTS.ACCEPTED_FORMATS.includes(file.type)) {
        resolve({
          type: 'format',
          message: 'Please upload a valid image format (JPEG, PNG, or WebP)',
        });
        return;
      }

      // Check file size
      if (file.size < IMAGE_REQUIREMENTS.MIN_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too small. Minimum size is ${
            IMAGE_REQUIREMENTS.MIN_SIZE / 1024
          }KB`,
        });
        return;
      }

      if (file.size > IMAGE_REQUIREMENTS.MAX_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too large. Maximum size is ${
            IMAGE_REQUIREMENTS.MAX_SIZE / (1024 * 1024)
          }MB`,
        });
        return;
      }

      // Check image dimensions
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        // For warning only (non-blocking)
        if (
          width < IMAGE_REQUIREMENTS.RECOMMENDED_WIDTH * 0.8 ||
          height < IMAGE_REQUIREMENTS.RECOMMENDED_HEIGHT * 0.8
        ) {
          resolve({
            type: 'dimensions',
            message: `For best quality, use ${IMAGE_REQUIREMENTS.RECOMMENDED_WIDTH}x${IMAGE_REQUIREMENTS.RECOMMENDED_HEIGHT}px. Current: ${width}x${height}px`,
          });
          return;
        }

        resolve(null); // No validation errors
      };

      img.onerror = () => {
        resolve({
          type: 'format',
          message: 'Unable to read image file. Please try a different image.',
        });
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const downloadAndUploadImageAsFile = async (imageUrl: string): Promise<void> => {
    try {
      setLoading(true);
      setValidationError(null);

      const response = await axios.get<Blob>(imageUrl, {
        responseType: 'blob',
      });

      const blob = response.data;

      const fileNameWithQuery = imageUrl.split('/').pop() || 'uploaded-file';
      const fileName = fileNameWithQuery.split('?')[0];
      const contentType = blob.type || 'application/octet-stream';

      const file = new File([blob], fileName, { type: contentType });

      if (file) {
        // Validate the downloaded image
        const validationResult = await validateImage(file);
        if (validationResult && validationResult.type !== 'dimensions') {
          setValidationError(validationResult);
          setLoading(false);
          return;
        }

        // Set warning for dimensions but continue
        if (validationResult && validationResult.type === 'dimensions') {
          setValidationError(validationResult);
        }

        const previewUrl = URL.createObjectURL(file);
        setPreviewImg(previewUrl);

        const s3Key = await uploadFileToS3(file);
        helper.setValue(s3Key);

        setLoading(false);
        setIsLocalSet(true);
      }
    } catch (error) {
      console.error('❌ Error in download/upload:', error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (field.value && isShopifyProduct && !isLocalSet) {
      downloadAndUploadImageAsFile(field.value);
    }
  }, [isShopifyProduct, field.value, isLocalSet]);

  const handleFileUpload = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      setIsLocalSet(true);
      setValidationError(null);

      const file = event.target.files ? event.target.files[0] : undefined;

      console.log('file', file);

      if (file) {
        setLoading(true);

        // Validate the image
        const validationResult = await validateImage(file);

        // Block upload for size and format errors
        if (validationResult && validationResult.type !== 'dimensions') {
          setValidationError(validationResult);
          setLoading(false);
          return;
        }

        // Show warning for dimensions but allow upload
        if (validationResult && validationResult.type === 'dimensions') {
          setValidationError(validationResult);
        }

        const dataUrl = URL.createObjectURL(file);
        setPreviewImg(dataUrl);

        try {
          const s3Key = await uploadFileToS3(file);
          helper.setValue(s3Key);
        } catch (error) {
          console.error('Upload failed:', error);
          setValidationError({
            type: 'size',
            message: 'Upload failed. Please try again or choose a different image.',
          });
        }

        setLoading(false);
      }
    },
    [helper, setPreviewImg],
  );

  React.useEffect(() => {
    if (!field.value) {
      setPreviewImg(undefined);
      setValidationError(null);
    }

    if (field.value && field.value.startsWith('https://')) {
      setPreviewImg(field.value);
    } else if (field.value && !field.value.startsWith('https://')) {
      setPreviewImg(`${baseUrl}/${field.value}`);
    }
  }, [field.value]);

  return (
    <Box sx={{ width: '100%' }}>
      <PhotoCard component='label' error={meta.touched && Boolean(meta.error)}>
        {previewImg ? (
          <Avatar
            src={previewImg}
            sx={{
              position: 'absolute',
              borderRadius: 'inherit',
              height: '100%',
              width: '100%',
              opacity: loading ? 0.5 : 1,
            }}
          />
        ) : (
          <Typography
            component='div'
            fontWeight='medium'
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            <Box component='img' src='/svg/upload.svg' sx={{ height: '2rem' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ textDecoration: 'underline', fontSize: '18px', fontWeight: 500 }}>
                Select a photo for your offer
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 300, color: '#848484', fontSize: '15px' }}>
                  Recommended size:{' '}
                  <Typography component={'span'} sx={{ fontWeight: 400 }}>
                    2000x2000 px
                  </Typography>
                </Typography>
                <Typography sx={{ color: '#848484', fontSize: '15px' }}>
                  Min. 200KB - Max 5MB
                </Typography>
              </Box>
            </Box>
          </Typography>
        )}
        {loading && <CircularProgress />}
        <input
          hidden
          accept='image/jpeg,image/jpg,image/png,image/webp'
          type='file'
          onChange={handleFileUpload}
        />
      </PhotoCard>

      {/* Validation Error Display */}
      {validationError && (
        <Alert
          severity={validationError.type === 'dimensions' ? 'warning' : 'error'}
          sx={{ mt: 2 }}
          onClose={() => setValidationError(null)}
        >
          {validationError.message}
        </Alert>
      )}

      {/* Formik Error Display */}
      {meta.touched && meta.error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {meta.error}
        </Alert>
      )}
    </Box>
  );
};

export default OfferPhoto;
